import { PureComponent, createRef } from "react";
import BasicHeaderComponent from "segments/desktop/header/layouts/components/basic-header/basic-header-component";
import { SIGNUP_BUTTON_TYPE } from "segments/desktop/core-components/button/button-consts";
import { withAbTests } from "/contexts/ab-tests-context";
import SecondaryHeaderDesktopComponent from "segments/desktop/header/layouts/base-header/secondary-header/secondary-header-desktop/secondary-header-desktop-component";
import {
  getMiniSiteUnifiedDesktopLogo,
  shouldHideGeneralPricingInMinisite,
  getClusterDesktopLogo,
  getMiniSiteLinks
} from "client/services/cluster-config-service/cluster-config-service";
import { withGenericConfig } from "/contexts/generic-config-context";

import { HEADER_SUBMENU_OPENED, HEADER_SUBMENU_CLOSED } from "/constants/bigbrain-event-types";
import { DESKTOP_HEADER_BASE_CLASS_NAME } from "segments/desktop/header/layouts/base-header/base-header-service";
import SignupPopupScreenComponent from "components/core/signups/signup-popup/signup-popup-screen-component";
import { SIGNUP_POPUP_CLOSED } from "constants/bigbrain-event-types.js";
import { shouldGetProductsLPsLogosTest } from "services/ab-tests/acquisition-ab-test-service";
import { CRM, MARKETING, SOFTWARE } from "constants/clusters";
import { PRODUCTS_LPS_LOGOS_TEST } from "constants/new-ab-tests";
import { isInTest } from "services/new-ab-tests/new-ab-tests-service";
import { componentStyles } from "./base-header-desktop-component.scss";
import LinkItemComponent from "./link-item/link-item-component";
import HeaderConstants from "../constants";
import UniverseHeader from "./universe-header/universe-header";
import { trackEvent } from "/services/bigbrain-service";

class BaseHeaderDesktopComponent extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      openedMenuId: null,
      showSignupPopup: false
    };

    this._scrollInfo = { scrollTopWhenStartedScrollingUp: 0, prevScrollTop: 0, scrollingDown: true };
    this._headerRef = createRef();
  }

  componentDidMount() {
    const { isSecondaryMenuNeeded } = this.props;
    if (isSecondaryMenuNeeded) {
      window.addEventListener("scroll", this.scrollListenerForSecondaryMenuUnification, {
        passive: true
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.scrollListenerForSecondaryMenuUnification);
    this.removeClickOutsideEvent();
  }

  removeClickOutsideEvent = () => {
    window.removeEventListener("mousedown", this.handleClickOutsideMenu);
  };

  handleClickOutsideMenu = (event) => {
    if (this?._headerRef?.current && !this._headerRef.current.contains(event.target)) {
      this.closeLinkMenu();
    }
  };

  scrollListenerForSecondaryMenuUnification = (event) => {
    {
      const header = document.querySelector(`.${DESKTOP_HEADER_BASE_CLASS_NAME}`);
      if (!header) {
        return;
      }

      let scrolledUpEnough = false;

      if (document.scrollingElement.scrollTop > this._scrollInfo.prevScrollTop) {
        // scrolling down
        this._scrollInfo.scrollingDown = true;
      } else {
        // scrolling up
        if (this._scrollInfo.scrollingDown) {
          this._scrollInfo.scrollTopWhenStartedScrollingUp = document.scrollingElement.scrollTop;
        }
        scrolledUpEnough =
          this._scrollInfo.scrollTopWhenStartedScrollingUp - document.scrollingElement.scrollTop >
          HeaderConstants.SCROLL_BACK_UP_THRESHOLD;

        this._scrollInfo.scrollingDown = false;
      }

      this._scrollInfo.prevScrollTop = document.scrollingElement.scrollTop;

      const showUnifiedBanner =
        document.scrollingElement.scrollTop > HeaderConstants.SCROLL_PIXEL_THRESHOLD &&
        (this._scrollInfo.scrollingDown || !scrolledUpEnough);

      if (document.scrollingElement.scrollTop > 0) {
        // shows the bottom menu border after scroll start
        header.classList.add("scroll-started");
      } else {
        header.classList.remove("scroll-started");
      }

      if (showUnifiedBanner) {
        header.classList.add("scrolled-threshold");
      } else {
        header.classList.remove("scrolled-threshold");
      }
    }
  };

  openLinkMenu(menuId) {
    const { supportHover } = this.props;
    if (!supportHover) {
      window.addEventListener("mousedown", this.handleClickOutsideMenu);
    }
    this.setState({
      openedMenuId: menuId
    });
    BigBrain("track", HEADER_SUBMENU_OPENED, { kind: menuId });

    this._basicHeader.forceShadow();
  }

  closeLinkMenu = (e) => {
    const { supportHover } = this.props;
    if (
      supportHover &&
      e?.relatedTarget?.closest &&
      e.relatedTarget.closest(`.${HeaderConstants.HEADER_MENU_COMPONENT_WRAPPER_CLASS_NAME}`)
    )
      return;

    const { openedMenuId } = this.state;
    if (!openedMenuId) return;

    if (!supportHover) {
      this.removeClickOutsideEvent();
    }

    this.setState({
      openedMenuId: null
    });

    BigBrain("track", HEADER_SUBMENU_CLOSED, { kind: openedMenuId });
    this._basicHeader.unforceShadow();
  };

  getButtonConfig() {
    const { buttonConfig } = this.props;
    const { buttonType } = buttonConfig;

    let extraConfigs;

    switch (buttonType) {
      case SIGNUP_BUTTON_TYPE: {
        extraConfigs = { buttonSize: "xs" };
        break;
      }

      default: {
        extraConfigs = {};
        break;
      }
    }

    return { ...buttonConfig, ...extraConfigs };
  }

  getUnifiedDesktopLogoIfExists = () => {
    const { isSecondaryMenuNeeded } = this.props;
    if (!isSecondaryMenuNeeded) return null;
    const { pageClusterConfig, useProductMiniSiteConfig } = this.props;
    return getMiniSiteUnifiedDesktopLogo(pageClusterConfig, { useProductMiniSiteConfig });
  };

  shouldShowMainPricingLink = () => {
    const { isSecondaryMenuNeeded, pageClusterConfig, useProductMiniSiteConfig } = this.props;

    if (!isSecondaryMenuNeeded) return true;
    return !shouldHideGeneralPricingInMinisite(pageClusterConfig, { useProductMiniSiteConfig });
  };

  renderUnifiedLinks = () => {
    const { pageClusterConfig, useProductMiniSiteConfig } = this.props;
    const links = getMiniSiteLinks(pageClusterConfig, { onlyUnifiedLinks: true, useProductMiniSiteConfig });

    return links.map((link) => (
      <div className="link-item-component-wrapper" key={link?.text}>
        <LinkItemComponent title={link?.text} url={link?.url} />
      </div>
    ));
  };

  renderBaseHeader = () => {
    const { showMenu, showContactSales, showButton, isSecondaryMenuNeeded, simulateBrandCampaign } = this.props;

    const { openedMenuId } = this.state;

    const universeHeaderProps = {
      showMenu,
      showContactSales,
      showButton,
      buttonConfig: this.getButtonConfig(),
      openedMenuId,
      openLinkMenu: this.openLinkMenu.bind(this),
      closeLinkMenu: this.closeLinkMenu.bind(this),
      shouldShowMainPricingLink: this.shouldShowMainPricingLink.bind(this),
      isSecondaryMenuNeeded,
      renderUnifiedLinks: this.renderUnifiedLinks.bind(this),
      openSignupPopup: this.openSignupPopup.bind(this),
      simulateBrandCampaign
    };

    return <UniverseHeader {...universeHeaderProps} />;
  };

  openSignupPopup = () => {
    this.setState({ showSignupPopup: true });
  };

  closeSignupPopup = (data) => {
    trackEvent(SIGNUP_POPUP_CLOSED, data);
    this.setState({ showSignupPopup: false });
  };

  isProductInClusterConfig = (productName) => {
    const { pageClusterId } = this.props;
    return pageClusterId === productName;
  };

  isCRMProductInClusterConfig = () => {
    return this.isProductInClusterConfig(CRM);
  };

  isMarketerProductInClusterConfig = () => {
    return this.isProductInClusterConfig(MARKETING);
  };

  isDevProductInClusterConfig = () => {
    return this.isProductInClusterConfig(SOFTWARE);
  };

  isEligibleForProductsLPsLogosTest = () => {
    const { abTests } = this.props;
    const isProductIncludedInTest =
      this.isCRMProductInClusterConfig() ||
      this.isMarketerProductInClusterConfig() ||
      this.isDevProductInClusterConfig();
    return isProductIncludedInTest && isInTest(PRODUCTS_LPS_LOGOS_TEST, abTests);
  };

  getMainPageLogo = () => {
    const { pageClusterConfig, logoHref, logoSrc, isSecondaryMenuNeeded, useProductMiniSiteConfig, abTests } =
      this.props;
    if (pageClusterConfig && !isSecondaryMenuNeeded) {
      const clusterMainLogo = getClusterDesktopLogo(pageClusterConfig, { useProductMiniSiteConfig });
      if (clusterMainLogo) {
        if (this.isEligibleForProductsLPsLogosTest()) {
          // trigger test
          const isNewVariant = shouldGetProductsLPsLogosTest(abTests, true);
          let logoSrc = clusterMainLogo.src;
          if (isNewVariant) {
            if (this.isCRMProductInClusterConfig()) {
              logoSrc =
                "https://dapulse-res.cloudinary.com/image/upload/Generator_featured%20images/Home%20Page%20-%202022%20Rebrand/header/LPs%20logos%20test/lp_monday_crm_logo_new_variant.png";
            }
            if (this.isMarketerProductInClusterConfig()) {
              logoSrc =
                "https://dapulse-res.cloudinary.com/image/upload/Generator_featured%20images/Home%20Page%20-%202022%20Rebrand/header/LPs%20logos%20test/lp_monday_marketer_logo_new_variant.png";
            }
            if (this.isDevProductInClusterConfig()) {
              logoSrc =
                "https://dapulse-res.cloudinary.com/image/upload/Generator_featured%20images/Home%20Page%20-%202022%20Rebrand/header/LPs%20logos%20test/lp_monday_dev_logo_new_variant.png";
            }
          } else {
            if (this.isCRMProductInClusterConfig()) {
              logoSrc =
                "https://dapulse-res.cloudinary.com/image/upload/Generator_featured%20images/Home%20Page%20-%202022%20Rebrand/header/LPs%20logos%20test/lp_monday_crm_logo_old_variant.png";
            }
            if (this.isMarketerProductInClusterConfig()) {
              logoSrc =
                "https://dapulse-res.cloudinary.com/image/upload/Generator_featured%20images/Home%20Page%20-%202022%20Rebrand/header/LPs%20logos%20test/lp_monday_marketer_logo_old_variant.png";
            }
            if (this.isDevProductInClusterConfig()) {
              logoSrc =
                "https://dapulse-res.cloudinary.com/image/upload/Generator_featured%20images/Home%20Page%20-%202022%20Rebrand/header/LPs%20logos%20test/lp_monday_dev_logo_old_variant.png";
            }
          }
          return { logoHref: clusterMainLogo.url, logoSrc, logoAltText: clusterMainLogo.alt };
        }
        return { logoHref: clusterMainLogo.url, logoSrc: clusterMainLogo.src, logoAltText: clusterMainLogo.alt };
      }
    }
    return { logoHref, logoSrc };
  };

  render() {
    const { isSecondaryMenuNeeded, logoPopup, translate } = this.props;
    const { openedMenuId, showSignupPopup } = this.state;

    const unifiedDesktopLogo = this.getUnifiedDesktopLogoIfExists();

    const mainLogo = this.getMainPageLogo();

    return (
      <header className={DESKTOP_HEADER_BASE_CLASS_NAME} ref={this._headerRef}>
        <BasicHeaderComponent
          ref={(element) => (this._basicHeader = element)}
          imageHref={mainLogo.logoHref}
          imageSrc={mainLogo.logoSrc}
          imageAlt={mainLogo.logoAltText}
          secondaryLogoObject={unifiedDesktopLogo}
          logoPopup={logoPopup}
          isMenuOpen={!!openedMenuId}
        >
          {this.renderBaseHeader()}
        </BasicHeaderComponent>

        {isSecondaryMenuNeeded && (
          <div className="secondary-header-wrapper">
            <SecondaryHeaderDesktopComponent />
          </div>
        )}
        {showSignupPopup && (
          <div className="signup-popup-wrapper">
            <SignupPopupScreenComponent translate={translate} closePopupAction={this.closeSignupPopup} />
          </div>
        )}
        <style jsx>{componentStyles}</style>
      </header>
    );
  }
}

BaseHeaderDesktopComponent.defaultProps = {
  showMenu: true,
  showButton: true,
  supportHover: false
};

export default withGenericConfig(withAbTests(BaseHeaderDesktopComponent));
